import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { BuildStamp, GithubMark } from "@/components/build-stamp";
import { LogoMark } from "@/components/logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Note } from "@/components/ui/note";
import { Separator } from "@/components/ui/separator";
import { authClient } from "@/lib/auth-client";
import { BRAND_NAME } from "@/lib/brand";
import { REPO_URL } from "@/lib/build-info";
import { trpc } from "@/lib/trpc";

export const Route = createFileRoute("/login")({
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ search }) => {
    const { data } = await authClient.getSession();
    if (data?.session) throw redirect({ to: search.redirect ?? "/devices" });
  },
  component: LoginPage,
});

function LoginPage() {
  const router = useRouter();
  const { redirect: redirectTo } = Route.useSearch();
  const { data: caps } = useQuery(trpc.user.capabilities.queryOptions());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"signIn" | "signUp">("signIn");

  const microsoft = useMutation({
    mutationFn: () =>
      authClient.signIn.social({
        provider: "microsoft",
        callbackURL: redirectTo ?? "/devices",
      }),
  });

  const credentials = useMutation({
    mutationFn: async () => {
      const fn =
        mode === "signIn"
          ? authClient.signIn.email({ email, password })
          : authClient.signUp.email({ email, password, name: email.split("@")[0] ?? email });
      const { error } = await fn;
      if (error) throw new Error(error.message ?? "Authentication failed");
    },
    onSuccess: async () => {
      await router.invalidate();
      await router.navigate({ to: redirectTo ?? "/devices" });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Email first, as Vercel's form is: one field and "Continue with Email",
  // the other ways in below a rule, and only then the password. Switching
  // between log in and sign up starts the form over.
  const [step, setStep] = useState<"email" | "password">("email");
  const toggleMode = () => {
    setMode(mode === "signIn" ? "signUp" : "signIn");
    setStep("email");
  };

  // Vercel's sign-in page, measured: no card; the mark alone in the top-left
  // corner and the way to the other form in the top-right; a 320px column on
  // the page colour whose title sits 146px from the top, every control 48px tall with
  // 16px text; a footnote at the foot.
  return (
    <div className="flex min-h-svh flex-col bg-background-200 px-6 pt-[env(safe-area-inset-top)] pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <header className="flex h-16 shrink-0 items-center justify-between">
        <LogoMark className="size-5 text-gray-1000" />
        <span className="sr-only">{BRAND_NAME}</span>
        {caps?.emailPassword && caps.signUp && (
          <Button
            variant="secondary"
            size="small"
            className="pointer-coarse:h-10"
            onClick={toggleMode}
          >
            {mode === "signIn" ? "Sign Up" : "Log In"}
          </Button>
        )}
      </header>

      <main className="flex flex-1 flex-col items-center pt-12 pb-16 md:pt-[62px]">
        <h1 className="mb-7 text-balance text-center text-gray-1000 text-heading-32">
          {mode === "signIn" ? `Log in to ${BRAND_NAME}` : `Sign up for ${BRAND_NAME}`}
        </h1>

        <div className="flex w-full max-w-[320px] flex-col">
          {caps?.emailPassword && (
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (step === "email") setStep("password");
                else credentials.mutate();
              }}
            >
              {/* Placeholders carry the names on screen, as Vercel's form does;
                  the labels stay for assistive tech. */}
              <Label htmlFor="email" className="sr-only">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                size="large"
                autoComplete="email"
                placeholder="Email Address"
                required
                autoFocus
                className="h-12 rounded-md"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {step === "password" && (
                <>
                  <Label htmlFor="password" className="sr-only">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    size="large"
                    autoComplete={mode === "signIn" ? "current-password" : "new-password"}
                    placeholder="Password"
                    required
                    minLength={8}
                    autoFocus
                    className="h-12 rounded-md"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </>
              )}
              <Button
                type="submit"
                size="large"
                className="h-12 w-full rounded-md"
                loading={credentials.isPending}
              >
                {step === "email"
                  ? "Continue with Email"
                  : mode === "signIn"
                    ? "Log In"
                    : "Sign Up"}
              </Button>
              {step === "password" && (
                <button
                  type="button"
                  className="mx-auto mt-3 rounded-sm px-1 text-copy-16 text-gray-1000 outline-none hover:text-gray-900 focus-visible:shadow-focus-ring"
                  onClick={() => {
                    setStep("email");
                    setPassword("");
                  }}
                >
                  ← Other login options
                </button>
              )}
            </form>
          )}

          {caps?.microsoft && step === "email" && (
            <>
              {caps.emailPassword && (
                <Separator className="my-6 bg-gray-alpha-400" aria-hidden="true" />
              )}
              <Button
                variant="secondary"
                size="large"
                className="h-12 w-full rounded-md"
                loading={microsoft.isPending}
                prefix={<MicrosoftLogo />}
                onClick={() => microsoft.mutate()}
              >
                Continue with Microsoft
              </Button>
            </>
          )}

          {/* Sign-ups are closed unless the coordinator says otherwise: an admin
              adds people, so the way to the other form only shows when it
              would succeed. */}
          {caps?.emailPassword && step === "email" && !caps.signUp && (
            <p className="mt-8 text-center text-copy-16 text-gray-900">
              No account? Ask an admin to add you.
            </p>
          )}

          {caps?.emailPassword && caps.signUp && step === "email" && (
            <p className="mt-8 text-center text-copy-16 text-gray-1000">
              {mode === "signIn" ? "Don't have an account? " : "Already have an account? "}
              {/* The padding is cancelled by the margin: a bigger touch
                  target that does not make the line taller. */}
              <button
                type="button"
                className="-my-2 rounded-sm py-2 text-blue-900 underline-offset-4 outline-none hover:underline focus-visible:shadow-focus-ring"
                onClick={toggleMode}
              >
                {mode === "signIn" ? "Sign Up" : "Log In"}
              </button>
            </p>
          )}

          {caps && !caps.microsoft && !caps.emailPassword && (
            <Note variant="error" role="alert">
              No sign-in method is configured. Set <code>MICROSOFT_CLIENT_ID</code>/
              <code>SECRET</code> or <code>ENABLE_EMAIL_PASSWORD</code> on the coordinator.
            </Note>
          )}
        </div>
      </main>

      {/* At the foot of the page, not under the heading: it is a footnote, and
          putting it between the product name and the first sign-in button read
          as part of the greeting. */}
      <footer className="flex items-center justify-center gap-4 text-gray-700 text-label-12">
        <BuildStamp className="text-gray-700" />
        <a
          href={REPO_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-sm outline-none transition-colors hover:text-gray-1000 focus-visible:shadow-focus-ring"
          aria-label="Source on GitHub"
        >
          <GithubMark className="size-3" />
          Source
        </a>
      </footer>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 23 23" aria-hidden="true" className="size-4">
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  );
}
